import { useEffect, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import PmsConfirmDialog from "../../Components/PmsConfirmDialog";
import { readStoredUser } from "../../utils/sessionUser";
import { managedByAdmin } from "../utils/tenderManagement";
import useCatalogAccess from "./useCatalogAccess";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "./CatalogCreationPendingNotice";
import { catalogCreationErrorText } from "./internalCatalogCodes";
import { itemCatalogText } from "./itemCatalogMessages";
import "../PurchasingUi.css";
import "./ItemAvailabilityControl.css";

export const isItemAvailabilityLocked = (item) => managedByAdmin(item) || Boolean(item?.merged_into_item_id);

const STATE_ERRORS = {
  INVALID_ITEM_STATE: "The item status is invalid. Reload and try again.",
  ITEM_STATE_ACTION_REQUIRED: "Use the item state action to deactivate or reactivate an item.",
  ITEM_NOT_FOUND: "Item not found.",
  CORPORATE_ITEM_READ_ONLY: "This corporate catalogue record can only be changed from general administration.",
  ITEM_MERGED: "This item has been merged. Change its principal item instead.",
  ITEM_MERGE_CONFIGURATION_LOCKED: "Separate the active item merge before changing item availability.",
  ITEM_ACCESS_DENIED: "Access denied for this item.",
};

export function ItemAvailabilityStatus({ is_active }) {
  const { themeId, languageId } = usePmsTheme();
  const active = is_active !== false;
  return <span className={`pilcore-purchasing-ui pu-item-availabilityStatus pu-item-availabilityStatus--${active ? "active" : "inactive"}`} data-theme={themeId}>
    {itemCatalogText(languageId, active ? "Active" : "Inactive")}
  </span>;
}

export default function ItemAvailabilityControl(props) {
  const actorId = readStoredUser()?.id;
  return <ItemAvailabilitySession key={`${actorId}:${props.businessUnitId}:${props.itemId}`} {...props} actorId={actorId} />;
}

function ItemAvailabilitySession({
  itemId, itemName, is_active, businessUnitId, canEdit = false,
  locked = false, showStatus = true, onUpdated, onBusyChange, onApprovalPending, actorId,
}) {
  const access = useCatalogAccess(businessUnitId);
  const permissions = ["purchasing.items.create", "purchasing.items.update", "purchasing.items.change_state"];
  const allowed = canEdit && access.can(permissions);
  const { themeId, languageId } = usePmsTheme();
  const t = (text) => itemCatalogText(languageId, catalogCreationErrorText(languageId, text));
  const [active, setActive] = useState(is_active !== false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pendingRequest, setPendingRequest] = useState(null);
  const pendingRef = useRef(false);
  const requestRef = useRef(0);
  const accessRef = useRef({ canEdit, locked });
  accessRef.current = { canEdit, locked };
  const validScope = Number.isSafeInteger(Number(itemId)) && Number(itemId) > 0
    && Number.isSafeInteger(Number(businessUnitId)) && Number(businessUnitId) > 0;

  useEffect(() => { setActive(is_active !== false); }, [is_active]);
  useEffect(() => { onBusyChange?.(saving); }, [saving, onBusyChange]);
  useEffect(() => () => { requestRef.current += 1; }, []);

  const sessionMatches = () => {
    const user = readStoredUser();
    return String(user?.id) === String(actorId)
      && Number(user?.active_business_unit_id) === Number(businessUnitId)
      && access.can(permissions)
      && accessRef.current.canEdit && !accessRef.current.locked;
  };

  const updateState = async (nextActive) => {
    if (pendingRef.current || !canEdit || locked || !validScope) return;
    if (!sessionMatches()) {
      setError("The session or business unit changed. Reload the catalog before changing this item.");
      return;
    }
    const stamp = access.capture();
    pendingRef.current = true;
    const request = ++requestRef.current;
    setSaving(true);
    setError("");
    try {
      const response = await axios.patch(`/api/purchasing/items/${itemId}/state`, {
        is_active: nextActive, business_unit_id: Number(businessUnitId),
      }, {
        headers: { "X-Pilcore-Business-Unit-Id": String(businessUnitId) },
        pilcoreExpectedUserId: actorId,
      });
      if (request !== requestRef.current || !sessionMatches() || !access.isCurrent(stamp, permissions)) return;
      if (isPendingCatalogCreation(response.data)) {
        setConfirmOpen(false);
        if (onApprovalPending) onApprovalPending(response.data.request);
        else setPendingRequest(response.data.request);
        return;
      }
      const updated = { ...response.data, id: itemId, is_active: response.data?.is_active ?? nextActive };
      setActive(updated.is_active);
      setConfirmOpen(false);
      onUpdated?.(updated);
    } catch (requestError) {
      if (request !== requestRef.current || !sessionMatches() || !access.isCurrent(stamp, permissions)) return;
      const failure = requestError.response?.data;
      setError(STATE_ERRORS[failure?.code] || failure?.error || "We could not change this item's status. Please try again.");
    } finally {
      if (request === requestRef.current) {
        pendingRef.current = false;
        setSaving(false);
      }
    }
  };

  const closeConfirmation = () => {
    if (pendingRef.current) return;
    setConfirmOpen(false);
    setError("");
  };

  return <div className="pilcore-purchasing-ui pu-item-availability" data-theme={themeId}>
    {showStatus && <ItemAvailabilityStatus is_active={active} />}
    {allowed && <button type="button" className="pu-item-availabilityAction"
      aria-label={`${t(active ? "Deactivate" : "Reactivate")} ${itemName || itemId}`}
      disabled={saving || Boolean(pendingRequest) || locked || !validScope} aria-busy={saving}
      title={locked ? t("This item is managed centrally or belongs to a completed merge.") : undefined}
      onClick={() => { setError(""); if (active) setConfirmOpen(true); else updateState(true); }}>
      {t(saving ? "Saving..." : active ? "Deactivate" : "Reactivate")}
    </button>}
    {error && (!confirmOpen || !allowed) && <div className="pu-item-availabilityError" role="alert">
      <span>{t(error)}</span>
      <button type="button" className="pu-item-availabilityAction" disabled={saving || !canEdit || locked}
        onClick={() => { if (active) setConfirmOpen(true); else updateState(true); }}>{t("Retry")}</button>
    </div>}
    {pendingRequest && <CatalogCreationPendingNotice request={pendingRequest} onClose={() => setPendingRequest(null)} />}
    <PmsConfirmDialog
      isOpen={confirmOpen && allowed && !locked}
      title={t("Deactivate item")}
      message={<>
        <p className="pcd-message"><strong>{itemName || itemId}</strong></p>
        <p className="pcd-message">{t("The item will no longer appear for new purchases. Its purchase history, movements and stock records will be preserved. You can reactivate it later.")}</p>
        {error && <p className="pu-item-availabilityDialogError" role="alert">{t(error)}</p>}
      </>}
      confirmLabel={t(saving ? "Saving..." : error ? "Retry" : "Deactivate")}
      cancelLabel={t("Cancel")}
      onConfirm={() => updateState(false)}
      onClose={closeConfirmation}
      isBusy={saving}
      zIndex={12500}
    />
  </div>;
}
